import React, { useMemo, useState } from 'react'
import { PlusIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingService } from '../../services/marketing'
import { BRAND_COLOR } from '../../constants'
import type { MarketingClippingCatalogItem, MarketingClippingCategory, MarketingClippingVehicle } from '../../types/marketing'

type CatalogKind = 'veiculo' | 'categoria'

interface ClippingCadastroBasicoTabProps {
  moduleName: string
  veiculos: MarketingClippingVehicle[]
  categorias: MarketingClippingCategory[]
  onVehiclesChange: (items: MarketingClippingVehicle[]) => void
  onCategoriesChange: (items: MarketingClippingCategory[]) => void
}

const PERM_LOADING: React.ReactNode = null

function catalogEntityLabel(kind: CatalogKind): string {
  return kind === 'veiculo' ? 'veículo' : 'categoria'
}

function catalogFormTitle(kind: CatalogKind, editing: boolean): string {
  if (kind === 'veiculo') return editing ? 'Editar veículo' : 'Novo veículo'
  return editing ? 'Editar categoria' : 'Nova categoria'
}

function catalogLogType(kind: CatalogKind) {
  if (kind === 'veiculo') return 'marketing_clipping_vehicle' as const
  return 'marketing_clipping_category' as const
}

function sortCatalog<T extends MarketingClippingCatalogItem>(list: T[]): T[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

const subTabClass = (active: boolean) =>
  `py-2.5 px-1 border-b-2 font-medium text-sm ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const ClippingCadastroBasicoTab: React.FC<ClippingCadastroBasicoTabProps> = ({
  moduleName,
  veiculos,
  categorias,
  onVehiclesChange,
  onCategoriesChange,
}) => {
  const { logCreate, logUpdate } = useUserLogs()
  const [activeSubTab, setActiveSubTab] = useState<CatalogKind>('veiculo')
  const [catalogName, setCatalogName] = useState('')
  const [catalogEditingId, setCatalogEditingId] = useState<string | null>(null)
  const [catalogFormOpen, setCatalogFormOpen] = useState(false)
  const [catalogSaving, setCatalogSaving] = useState(false)
  const [catalogTogglingId, setCatalogTogglingId] = useState<string | null>(null)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const items = useMemo(
    () => (activeSubTab === 'veiculo' ? veiculos : categorias),
    [activeSubTab, veiculos, categorias]
  )

  const switchSubTab = (kind: CatalogKind) => {
    setActiveSubTab(kind)
    setCatalogName('')
    setCatalogEditingId(null)
    setCatalogFormOpen(false)
    setCatalogError(null)
  }

  const openNew = () => {
    setCatalogEditingId(null)
    setCatalogName('')
    setCatalogError(null)
    setCatalogFormOpen(true)
  }

  const startEdit = (item: MarketingClippingCatalogItem) => {
    setCatalogEditingId(item.id)
    setCatalogName(item.name)
    setCatalogError(null)
    setCatalogFormOpen(true)
  }

  const closeForm = () => {
    setCatalogFormOpen(false)
    setCatalogEditingId(null)
    setCatalogName('')
    setCatalogError(null)
  }

  const save = async () => {
    const name = catalogName.trim()
    if (!name) {
      setCatalogError('Informe o nome')
      return
    }
    try {
      setCatalogSaving(true)
      setCatalogError(null)

      if (catalogEditingId) {
        const previous = items.find((row) => row.id === catalogEditingId)
        let updated: MarketingClippingCatalogItem
        if (activeSubTab === 'veiculo') {
          updated = await marketingService.updateVehicle(catalogEditingId, { name })
          onVehiclesChange(sortCatalog(veiculos.map((row) => (row.id === updated.id ? updated : row))))
        } else {
          updated = await marketingService.updateCategory(catalogEditingId, { name })
          onCategoriesChange(sortCatalog(categorias.map((row) => (row.id === updated.id ? updated : row))))
        }
        void logUpdate(
          catalogLogType(activeSubTab),
          updated.id,
          `Atualizou ${catalogEntityLabel(activeSubTab)} de clipping: ${updated.name}`,
          previous ? { name: previous.name, is_active: previous.is_active } : undefined,
          { name: updated.name, is_active: updated.is_active }
        )
        closeForm()
        return
      }

      if (activeSubTab === 'veiculo') {
        const created = await marketingService.createVehicle({ name })
        onVehiclesChange(sortCatalog([...veiculos, created]))
        void logCreate(
          catalogLogType('veiculo'),
          created.id,
          `Criou veículo de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      } else {
        const created = await marketingService.createCategory({ name })
        onCategoriesChange(sortCatalog([...categorias, created]))
        void logCreate(
          catalogLogType('categoria'),
          created.id,
          `Criou categoria de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      }
      closeForm()
    } catch (err: unknown) {
      setCatalogError(err instanceof Error ? err.message : 'Erro ao salvar')
    } finally {
      setCatalogSaving(false)
    }
  }

  const toggleActive = async (item: MarketingClippingCatalogItem) => {
    const nextActive = !item.is_active
    const label = catalogEntityLabel(activeSubTab)
    try {
      setCatalogTogglingId(item.id)
      setCatalogError(null)
      let updated: MarketingClippingCatalogItem
      if (activeSubTab === 'veiculo') {
        updated = await marketingService.updateVehicle(item.id, { is_active: nextActive })
        onVehiclesChange(sortCatalog(veiculos.map((row) => (row.id === updated.id ? updated : row))))
      } else {
        updated = await marketingService.updateCategory(item.id, { is_active: nextActive })
        onCategoriesChange(sortCatalog(categorias.map((row) => (row.id === updated.id ? updated : row))))
      }
      void logUpdate(
        catalogLogType(activeSubTab),
        updated.id,
        `${nextActive ? 'Ativou' : 'Inativou'} ${label} de clipping: ${updated.name}`,
        { name: item.name, is_active: item.is_active },
        { name: updated.name, is_active: updated.is_active }
      )
    } catch (err: unknown) {
      setCatalogError(err instanceof Error ? err.message : 'Erro ao atualizar status')
    } finally {
      setCatalogTogglingId(null)
    }
  }

  return (
    <div>
      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Subabas de cadastro básico">
          <button
            type="button"
            onClick={() => switchSubTab('veiculo')}
            className={subTabClass(activeSubTab === 'veiculo')}
          >
            Veículos
          </button>
          <button
            type="button"
            onClick={() => switchSubTab('categoria')}
            className={subTabClass(activeSubTab === 'categoria')}
          >
            Categorias
          </button>
        </nav>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              {activeSubTab === 'veiculo' ? 'Veículos' : 'Categorias'}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              {activeSubTab === 'veiculo'
                ? 'Veículos usados no vínculo dos sites e dos clippings.'
                : 'Categorias usadas na classificação dos clippings.'}
            </Text>
          </div>
          <PermissionGuard
            moduleName={moduleName}
            permissionType="insert"
            fallback={null}
            loadingComponent={PERM_LOADING}
          >
            <Button
              onClick={openNew}
              className="flex items-center gap-1 shrink-0"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <PlusIcon className="h-5 w-5" />
              {activeSubTab === 'veiculo' ? 'Novo veículo' : 'Nova categoria'}
            </Button>
          </PermissionGuard>
        </div>
        {catalogError && !catalogFormOpen && (
          <div className="px-6 pt-4">
            <Text size="sm" className="text-red-500">
              {catalogError}
            </Text>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nenhum registro cadastrado.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                      catalogEditingId === item.id ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          item.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'
                        }`}
                      >
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 flex-nowrap whitespace-nowrap">
                        <PermissionGuard
                          moduleName={moduleName}
                          permissionType="update"
                          fallback={null}
                          loadingComponent={PERM_LOADING}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(item)}
                            disabled={catalogSaving || Boolean(catalogTogglingId)}
                            title="Editar"
                            className="shrink-0"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard
                          moduleName={moduleName}
                          permissionType="update"
                          fallback={null}
                          loadingComponent={PERM_LOADING}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void toggleActive(item)}
                            disabled={catalogTogglingId === item.id || catalogSaving}
                            className="shrink-0"
                          >
                            {catalogTogglingId === item.id
                              ? 'Salvando...'
                              : item.is_active
                                ? 'Inativar'
                                : 'Ativar'}
                          </Button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {catalogFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {catalogFormTitle(activeSubTab, Boolean(catalogEditingId))}
              </Heading>
              <Button
                onClick={closeForm}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                disabled={catalogSaving}
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {catalogError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                  {catalogError}
                </div>
              )}
              <div>
                <Text size="sm" className="mb-2">
                  Nome <span className="text-red-500">*</span>
                </Text>
                <Input
                  value={catalogName}
                  onChange={(e) => setCatalogName(e.target.value)}
                  placeholder="Digite o nome..."
                  className={catalogError ? 'border-red-500' : ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void save()
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={closeForm} variant="outline" disabled={catalogSaving}>
                Cancelar
              </Button>
              <PermissionGuard
                moduleName={moduleName}
                permissionType={catalogEditingId ? 'update' : 'insert'}
                fallback={null}
                loadingComponent={PERM_LOADING}
              >
                <Button
                  onClick={() => void save()}
                  disabled={catalogSaving || !catalogName.trim()}
                  className="flex items-center gap-2"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  {catalogSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      {catalogEditingId ? 'Atualizar' : 'Criar'}
                    </>
                  )}
                </Button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClippingCadastroBasicoTab
